/**
 * finance-categories.ts — D43: danh mục thu/chi được QUẢN TRỊ (danh sách đóng).
 * Chống bùng nổ danh mục khi scale: form giao dịch chỉ chọn từ danh sách này,
 * thêm/sửa/ẩn/xoá ở màn "Quản lý danh mục" (finance-categories-modal).
 *
 * Pure helpers — không side effect:
 * - financeCategoryId: id deterministic theo slug tên → seed trên nhiều thiết bị
 *   hội tụ về cùng id khi sync (upsert, không nhân đôi).
 * - buildSeedFinanceCategories: bộ chuẩn VN + hấp thụ danh mục cũ đang dùng
 *   trong budgets/transactions/recurring (giữ tương thích dữ liệu hiện có).
 * - activeFinanceCategoryNames: tên cho dropdown theo loại giao dịch.
 */
import type { FinanceCategory, LifeOsState, TransactionType } from "../types";

// Icon = tên trong bộ FINANCE_CATEGORY_ICONS (finance-category-visual.tsx).
export const STANDARD_FINANCE_CATEGORIES: Array<{ name: string; kind: TransactionType; icon: string; color: string }> = [
  { name: "Ăn uống", kind: "expense", icon: "utensils", color: "#f59e0b" },
  { name: "Đi lại", kind: "expense", icon: "bus", color: "#3b82f6" },
  { name: "Nhà ở", kind: "expense", icon: "home", color: "#8b5cf6" },
  { name: "Hoá đơn & Tiện ích", kind: "expense", icon: "zap", color: "#eab308" },
  { name: "Mua sắm", kind: "expense", icon: "shopping-bag", color: "#ec4899" },
  { name: "Sức khoẻ", kind: "expense", icon: "heart-pulse", color: "#ef4444" },
  { name: "Giáo dục", kind: "expense", icon: "graduation-cap", color: "#06b6d4" },
  { name: "Giải trí", kind: "expense", icon: "gamepad", color: "#a855f7" },
  { name: "Du lịch", kind: "expense", icon: "plane", color: "#14b8a6" },
  { name: "Quà & Từ thiện", kind: "expense", icon: "gift", color: "#f43f5e" },
  { name: "Đầu tư", kind: "expense", icon: "trending-up", color: "#10b981" },
  { name: "Khác", kind: "expense", icon: "tag", color: "#94a3b8" },
  { name: "Lương", kind: "income", icon: "banknote", color: "#10b981" },
  { name: "Thưởng", kind: "income", icon: "award", color: "#84cc16" },
  { name: "Thu nhập khác", kind: "income", icon: "piggy-bank", color: "#34d399" },
];

/** Bảng màu xoay vòng cho danh mục user tự thêm / hấp thụ từ dữ liệu cũ. */
export const CATEGORY_COLOR_PALETTE = [
  "#7c3aed", "#ec4899", "#10b981", "#f59e0b", "#06b6d4", "#f43f5e", "#84cc16", "#6366f1",
];

/** Slug hoá tên (bỏ dấu tiếng Việt) → id ổn định giữa các thiết bị. */
export function financeCategoryId(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `fincat_${slug || "khac"}`;
}

const SEED_LEGACY_CAP = 30;

/**
 * Bộ seed: danh mục chuẩn + danh mục cũ đang dùng (kind suy từ chỗ dùng:
 * transactions theo type, budgets/recurring-expense → expense).
 */
export function buildSeedFinanceCategories(
  state: Pick<LifeOsState, "budgets" | "transactions" | "recurring">,
  nowIso = new Date().toISOString(),
): FinanceCategory[] {
  const byId = new Map<string, FinanceCategory>();
  const push = (name: string | undefined, kind: TransactionType, icon?: string, color?: string) => {
    const trimmed = name?.trim();
    if (!trimmed) return;
    const id = financeCategoryId(trimmed);
    if (byId.has(id)) return;
    byId.set(id, {
      id,
      name: trimmed,
      kind,
      icon: icon ?? "tag",
      color: color ?? CATEGORY_COLOR_PALETTE[byId.size % CATEGORY_COLOR_PALETTE.length],
      createdAt: nowIso,
    });
  };
  for (const c of STANDARD_FINANCE_CATEGORIES) push(c.name, c.kind, c.icon, c.color);

  let legacyAdded = 0;
  const pushLegacy = (name: string | undefined, kind: TransactionType) => {
    if (legacyAdded >= SEED_LEGACY_CAP) return;
    const before = byId.size;
    push(name, kind);
    if (byId.size > before) legacyAdded++;
  };
  for (const b of state.budgets) pushLegacy(b.category, "expense");
  for (const t of state.transactions) {
    if (t.toAccountId) continue; // transfer không có danh mục thật
    pushLegacy(t.category, t.type === "income" ? "income" : "expense");
  }
  for (const r of state.recurring) pushLegacy(r.category, r.type === "income" ? "income" : "expense");

  return [...byId.values()];
}

/** Tên danh mục đang hoạt động (không ẩn) cho dropdown, theo loại giao dịch. */
export function activeFinanceCategoryNames(
  state: Pick<LifeOsState, "financeCategories">,
  kind: TransactionType,
): string[] {
  return state.financeCategories
    .filter((c) => c.kind === kind && !c.archived)
    .map((c) => c.name);
}
